// src/pages/lawyer/components/LawyerDetails.jsx
import { useState, useEffect } from "react"
import { Card, Button, Dropdown, Badge } from "react-bootstrap"
import { useNavigate, useParams, Link } from "react-router-dom"
import Layout from "../../../layout/default"
import Block from "../../../components/Block/Block"
import DataTable from "../../../components/DataTable/DataTable.js"
import { Descriptions } from "antd"
import {
  Icon,
  CustomDropdownToggle,
  CustomDropdownMenu,
  LinkList,
} from "../../../components"

import lawyerService from "../services/lawyerService"
import leadService from "../../lead/services/leadService.js"
import { COLOR_STATE_MAP } from "../../../constants/stateColors.js"
import caseService from "../services/caseService.js"
import style from "react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark"

/* ----------------------------- Small helpers ----------------------------- */

const fmtDate = (iso) => {
  if (!iso) return "-"
  const d = new Date(iso)
  return d.toLocaleString("es-ES", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const asCurrency = (num) =>
  typeof num === "number"
    ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(num)
    : "-"

const StatePill = ({ text }) => (
  <Badge bg="" style={{ backgroundColor: "#5f38f9", color: "white", letterSpacing: ".3px" }}>
    {text}
  </Badge>
)

/* --------------------------- Shared comment list -------------------------- */

const CommentList = ({ comments = [] }) => {
  if (!Array.isArray(comments) || comments.length === 0) {
    return <p className="p-3">Sin comentarios</p>
  }
  return (
    <div className="p-3" style={{ background: "#fafbff" }}>
      <div className="fw-semibold mb-2">Comentarios</div>
      <ul className="data-details gap gy-1 small m-0">
        {comments
          .slice()
          .reverse()
          .map((c) => (
            <li key={c.id} className="py-2 px-3 mb-2 rounded-3 border bg-white">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="text-muted small">{fmtDate(c.message_date)}</span>
                {c.comment_type_name && (
                  <Badge bg="info" title="Tipo de comentario" className="rounded-1">
                    {c.comment_type_name}
                  </Badge>
                )}
              </div>
              <div className="text-body">{c.message}</div>
            </li>
          ))}
      </ul>
    </div>
  )
}

/* ------------------------- Lawyer header/details ------------------------- */

const LawyerInfo = ({
  lawyerData,
  UserList,
  Lawyer_schoolList,
  Posts_planList,
}) => {
  const navigate = useNavigate()
  return (
    <Descriptions
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px",
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            backgroundColor: "#f9f9f9",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                backgroundColor: lawyerData?.image_url ? "transparent" : "gray",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                marginRight: "20px",
                border: "2px solid #ccc",
              }}
            >
              {lawyerData.image_url ? (
                <img
                  src={lawyerData.image_url}
                  alt="Perfil abogado"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span style={{ color: "white", fontSize: "24px" }}>AB</span>
              )}
            </div>

            <h4
              style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "500",
                color: "#333",
              }}
            >
              {lawyerData.name} {lawyerData.last_name}
            </h4>
          </div>

          <Button
            variant="primary"
            onClick={() => navigate("/lawyer/edit/" + lawyerData.id)}
            style={{
              padding: "10px 20px",
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "8px",
              color: "#fff",
            }}
          >
            Actualizar Perfil
          </Button>
        </div>
      }
      layout="vertical"
    >
      <Descriptions.Item label="Nombre">{lawyerData.name}</Descriptions.Item>
      <Descriptions.Item label="Apellidos">
        {lawyerData.last_name}
      </Descriptions.Item>
      <Descriptions.Item label="Teléfono">
        {lawyerData.phone_number}
      </Descriptions.Item>
      <Descriptions.Item label="Email">{lawyerData.email}</Descriptions.Item>
      <Descriptions.Item label="Dirección de Negocio">
        {lawyerData.business_direction}
      </Descriptions.Item>
      <Descriptions.Item label="Nombre de Negocio">
        {lawyerData.business_name}
      </Descriptions.Item>
      <Descriptions.Item label="Código de Escuela de Abogados">
        {lawyerData.lawyer_school_code}
      </Descriptions.Item>
      <Descriptions.Item label="Acerca de mí">
        {lawyerData.about_me}
      </Descriptions.Item>
      <Descriptions.Item label="Usuario">
        {UserList?.find((user) => user.id === lawyerData.user)?.email || "N/A"}
      </Descriptions.Item>
      <Descriptions.Item label="Escuela de Abogados">
        {Lawyer_schoolList?.find(
          (school) => school.id === lawyerData.lawyer_school
        )?.name || "N/A"}
      </Descriptions.Item>
      <Descriptions.Item label="Provincia">
        {lawyerData.locality ? lawyerData.locality : "N/A"}
      </Descriptions.Item>
      <Descriptions.Item label="Directorio">
        {lawyerData.default_directory_visibility ? "Sí" : "No"}
      </Descriptions.Item>
      <Descriptions.Item label="Página Web">
        {lawyerData.website}
      </Descriptions.Item>
      <Descriptions.Item label="Nivel de Optimización">
        {lawyerData.optimization_level}%
      </Descriptions.Item>
      <Descriptions.Item label="Click Completar más tarde">
        {lawyerData.complete_later ? "Sí" : "No"}
      </Descriptions.Item>
    </Descriptions>
  )
}

/* ----------------------- Lead row expanded component ---------------------- */

const leadExpandedComponent = ({ data }) => {
  const comments = Array.isArray(data.lead_comment) ? data.lead_comment : []
  return <CommentList comments={comments} />
}

/* ---------------------------- Main component ----------------------------- */

function LawyerDetails() {
  const { id } = useParams()

  // Lawyer + lists
  const [lawyerData, setLawyerData] = useState({
    name: "",
    last_name: "",
    phone_number: "",
    email: "",
    image: "",
    business_direcction: "",
    business_name: "",
    lawyer_school_code: "",
    about_me: "",
    complete_later: false,
    default_directory_visibility: false,
    is_active: true,
    website: "https://abogueo.com/",
  })
  const [UserList, setUserList] = useState([])
  const [Lawyer_schoolList, setLawyer_schoolList] = useState([])
  const [Posts_planList, setPosts_planList] = useState([])

  // Loading
  const [initialLoading, setInitialLoading] = useState(false)

  // Leads
  const [leadData, setLead] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)

  // Shared reference lists
  const [specialtyData, setSpecialty] = useState([])
  const [provinceData, setProvince] = useState([])

  // Cases
  const [caseData, setCaseData] = useState([])
  const [caseLoading, setCaseLoading] = useState(false)
  const [casePage, setCasePage] = useState(1)
  const [caseCount, setCaseCount] = useState(0)

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchAll = async () => {
    try {
      setInitialLoading(true)
      setLoading(true)
      setCaseLoading(true)
      await Promise.all([
        fetchLawyer(),
        fetchUserList(),
        fetchLawyer_schoolList(),
        fetchPosts_planList(),
        fetchSpecialtyList(),
        fetchProvinceList(),
      ])
    } catch (error) {
      console.log("Error fetching lawyer:", error)
    } finally {
      setInitialLoading(false)
      setLoading(false)
      setCaseLoading(false)
    }
  }

  /* ----------------------------- Fetch helpers ---------------------------- */

  const fetchLawyer = async () => {
    try {
      const lawyer = await lawyerService.getAllLawyerDetails(id)
      if (!lawyer.website) {
        lawyer.website = "https://abogueo.com/"
      }
      setLawyerData(lawyer)

      // Initial dependent loads
      await Promise.all([
        fetchLeadList(1, {}, 10, lawyer.id),
        fetchCaseList(1, {}, 10, lawyer.id),
      ])
    } catch (error) {
      console.log("Error fetching lawyer:", error)
    }
  }

  const fetchUserList = async () => {
    try {
      const response = await lawyerService.getUserList()
      setUserList(response)
    } catch (error) {
      console.error("Error fetching user list:", error)
    }
  }

  const fetchLawyer_schoolList = async () => {
    try {
      const response = await lawyerService.getLawyer_schoolList()
      setLawyer_schoolList(response)
    } catch (error) {
      console.error("Error fetching lawyer school list:", error)
    }
  }

  const fetchPosts_planList = async () => {
    try {
      const response = await lawyerService.getPosts_planList()
      setPosts_planList(response)
    } catch (error) {
      console.error("Error fetching posts plan list:", error)
    }
  }

  const fetchSpecialtyList = async () => {
    const specialty = await leadService.getSpecialtyList()
    setSpecialty(specialty)
  }

  const fetchProvinceList = async () => {
    const province = await leadService.getProvinceList()
    setProvince(province)
  }

  /* -------------------------------- Leads -------------------------------- */

  const fetchLeadList = async (
    pageNumber = 1,
    filters = {},
    limit = 10,
    lawyerId = lawyerData?.id
  ) => {
    const response = await leadService.getLeadListPaginateByLawyer(
      pageNumber,
      { ...filters, lawyer: lawyerId },
      limit
    )
    setLead(response.results)
    setCount(response.count)
    setPage(pageNumber)
  }

  const paginateLeadList = async (pageNumber = 1) => {
    try {
      setLoading(true)
      await fetchLeadList(pageNumber)
    } catch (error) {
      console.log("Error fetching lead list:", error)
    } finally {
      setLoading(false)
    }
  }

  /* -------------------------------- Cases -------------------------------- */

  const fetchCaseList = async (
    pageNumber = 1,
    filters = {},
    limit = 10,
    lawyerId = lawyerData?.id
  ) => {
    const response = await caseService.getCasesByLawyer({
      lawyerId,
      page: pageNumber,
      limit,
      filters,
    })
    setCaseData(response.results || [])
    setCaseCount(response.count || 0)
    setCasePage(pageNumber)
  }

  const paginateCaseList = async (pageNumber = 1) => {
    try {
      setCaseLoading(true)
      await fetchCaseList(pageNumber)
    } catch (error) {
      console.log("Error fetching case list:", error)
    } finally {
      setCaseLoading(false)
    }
  }

  /* ------------------------------- Columns -------------------------------- */

  const leadColumns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
    },
    {
      name: "Fecha",
      selector: (row) => row.assignation_date || "",
      sortable: true,
      cell: (row) => <span title={row.assignation_date}>{fmtDate(row.assignation_date)}</span>,
    },
    {
      name: "Cliente",
      selector: (row) => `${row.name || ""} ${row.last_name || ""}`.trim(),
      sortable: true,
      cell: (row) => (
        <div className="d-flex flex-column">
          <div className="fw-semibold">
            {[row.name, row.last_name].filter(Boolean).join(" ") || "-"}
          </div>
          <div className="text-muted small d-flex gap-2 flex-wrap">
            {row.email && <span>{row.email}</span>}
            {row.phone_number && <span>· {row.phone_number}</span>}
          </div>
        </div>
      ),
    },
    {
      name: "Especialidad",
      selector: (row) => {
        const spec = specialtyData.find((s) => s.id === row.specialty)
        return spec ? spec.child_specialty : ""
      },
      sortable: true,
      cell: (row) => {
        const spec = specialtyData.find((s) => s.id === row.specialty)
        const label = spec ? spec.child_specialty : ""
        return label || "-"
      },
      wrap: true,
    },
    {
      name: "Provincia",
      selector: (row) => {
        const prov = provinceData.find((p) => p.id === row.province)
        return prov ? prov.name : ""
      },
      sortable: true,
      cell: (row) => {
        const prov = provinceData.find((p) => p.id === row.province)
        const name = prov ? prov.name : ""
        return name || "-"
      },
    },
    {
      name: "SMS",
      selector: (row) => (row.sms_verified ? 1 : 0),
      sortable: true,
      cell: (row) =>
        row.sms_verified ? <Badge bg="success">Verificado</Badge> : <Badge bg="warning">No</Badge>,
    },
    {
      name: "Estado",
      selector: (row) => row?.state_assignment?.state?.state || "",
      sortable: true,
      cell: (row) => {
        if (!row.state_assignment) return <StatePill text="Sin estados" />
        const stateName = row?.state_assignment?.state?.state || ""
        const substateName = row?.state_assignment?.substate?.substate || ""
        const color = COLOR_STATE_MAP[stateName] || "#5f38f9"
        return (
          <Badge bg="" style={{ backgroundColor: color }}>
            {substateName ? `${stateName} | ${substateName}` : stateName}
          </Badge>
        )
      },
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="text-end w-100">
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              as={CustomDropdownToggle}
              className="btn btn-sm btn-icon btn-zoom me-n1"
            >
              <Icon name="more-v" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-sm" as={CustomDropdownMenu} align="end">
              <div className="dropdown-content py-1">
                <LinkList className="link-list-hover-bg-primary link-list-md">
                  <Link to={`/lead/edit/${row.id}`} className="dropdown-item">
                    <Icon name="edit" />
                    <span>Edit</span>
                  </Link>
                </LinkList>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ]

  const caseColumns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
    },
    {
      name: "Caso",
      selector: (row) => row.case_number || "-",
      sortable: true,
      cell: (row) =>
        row.case_number ? (
          <Badge bg="dark" title="Nº de caso" className="rounded-1">
            {row.case_number}
          </Badge>
        ) : (
          "-"
        ),
    },
    {
      name: "Cliente",
      selector: (row) => {
        const lead = row.lead || {}
        return [lead.name, lead.last_name].filter(Boolean).join(" ")
      },
      sortable: true,
      cell: (row) => {
        const lead = row.lead || {}
        const fullName = [lead.name, lead.last_name].filter(Boolean).join(" ")
        return (
          <div className="d-flex flex-column">
            <div className="fw-semibold">{fullName || "-"}</div>
            <div className="text-muted small d-flex gap-2 flex-wrap">
              {lead.email && <span>{lead.email}</span>}
              {lead.phone_number && <span>· {lead.phone_number}</span>}
            </div>
          </div>
        )
      },
    },
    {
      name: "Especialidad",
      selector: (row) =>
        row.lead?.specialty?.child_specialty || row.lead?.main_specialty_name || "",
      sortable: true,
      cell: (row) => {
        const label =
          row.lead?.specialty?.child_specialty || row.lead?.main_specialty_name || "-"
        return label
      },
      wrap: true,
    },
    {
      name: "Provincia",
      selector: (row) => row.lead?.province_detail?.name || row.province_detail?.name || "",
      sortable: true,
      cell: (row) => {
        const name = row.lead?.province_detail?.name || row.province_detail?.name || "-"
        return name
      },
      wrap: true,
    },
    {
      name: "Estado",
      selector: (row) => row.state_detail?.state || "",
      sortable: true,
      cell: (row) => {
        const stateName = row.state_detail?.state || ""
        const substateName = row.substate_detail?.substate || ""
        const color = COLOR_STATE_MAP[stateName] || "#5f38f9"
        return stateName ? (
          <Badge bg="" style={{ backgroundColor: color }}>
            {substateName ? `${stateName} | ${substateName}` : stateName}
          </Badge>
        ) : (
          <StatePill text="Sin estados" />
        )
      },
    },
    {
      name: "Inicio",
      selector: (row) => row.start_date || "",
      sortable: true,
      cell: (row) => <span title={row.start_date}>{fmtDate(row.start_date)}</span>,
    },
    {
      name: "Comentarios",
      selector: (row) => (Array.isArray(row.lead_comments) ? row.lead_comments.length : 0),
      sortable: true,
      cell: (row) => {
        const total = Array.isArray(row.lead_comments) ? row.lead_comments.length : 0
        const has = total > 0
        const last = has ? row.lead_comments[0] : null // ordered desc in API
        return (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Badge bg={has ? "primary" : "secondary"} className="rounded-1">
              {total} {total === 1 ? "comentario" : "comentarios"}
            </Badge>
            {last?.comment_type_name && (
              <Badge bg="info" title="Tipo del último" className="rounded-1">
                {last.comment_type_name}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="text-end w-100">
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              as={CustomDropdownToggle}
              className="btn btn-sm btn-icon btn-zoom me-n1"
            >
              <Icon name="more-v" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-sm" as={CustomDropdownMenu} align="end">
              <div className="dropdown-content py-1">
                <LinkList className="link-list-hover-bg-primary link-list-md">
                  <Link to={`/case/edit/${row.id}`} className="dropdown-item">
                    <Icon name="edit" />
                    <span>Edit</span>
                  </Link>
                </LinkList>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ]

  const caseExpandedComponent = ({ data }) => {
    const comments = Array.isArray(data.lead_comments) ? data.lead_comments : []
    return <CommentList comments={comments} />
  }

  const pageTitle = "Perfil Abogado"

  /* -------------------------------- Render -------------------------------- */

  return (
    <Layout title={pageTitle} content="container" loading={initialLoading}>
      <Block>
        <Card className="card-gutter-md">
          <Card.Body>
            <div className="bio-block">
              <LawyerInfo
                lawyerData={lawyerData}
                UserList={UserList}
                Lawyer_schoolList={Lawyer_schoolList}
                Posts_planList={Posts_planList}
              />
            </div>
          </Card.Body>
        </Card>
      </Block>

      <Block>
        <Card className="card-gutter-md">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="bio-block-title mb-4">Leads asignados</h4>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="dark" className="rounded-0 px-2 py-1" title="Total leads">
                  {count} en total
                </Badge>
                <Button size="sm" variant="outline-secondary" onClick={() => paginateLeadList(page)}>
                  <Icon name="refresh" />
                  <span className="ms-1">Actualizar</span>
                </Button>
              </div>
            </div>

            {!loading && (
              <DataTable
                tableClassName="data-table-head-light table-responsive"
                data={leadData}
                columns={leadColumns}
                expandableRows
                actions
                searchByList={["name", "last_name", "email", "phone_number"]}
                backEndPagination={true}
                fetchPaginatedData={paginateLeadList}
                pageCount={count}
                currentPaginationPage={page}
                backendFilter={true}
                filterFunc={fetchLeadList}
                isLeadForm={true}
                customExpandedComponent={leadExpandedComponent}
              />
            )}
          </Card.Body>
        </Card>
      </Block>

      <Block>
        <Card className="card-gutter-md">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="bio-block-title mb-4">Casos del abogado</h4>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="dark" className="rounded-0 px-2 py-1" title="Total casos">
                  {caseCount} en total
                </Badge>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => paginateCaseList(casePage)}
                >
                  <Icon name="refresh" />
                  <span className="ms-1">Actualizar</span>
                </Button>
              </div>
            </div>

            {!caseLoading && (
              <DataTable
                tableClassName="data-table-head-light table-responsive"
                data={caseData}
                columns={caseColumns}
                actions
                searchByList={[
                  "title",
                  "reference",
                  "name",
                  "client_name",
                  "case_number",
                ]}
                backEndPagination={true}
                fetchPaginatedData={paginateCaseList}
                pageCount={caseCount}
                customExpandedComponent={caseExpandedComponent}
                currentPaginationPage={casePage}
                backendFilter={true}
                filterFunc={fetchCaseList}
              />
            )}
          </Card.Body>
        </Card>
      </Block>
    </Layout>
  )
}

export default LawyerDetails
